/**
 * TodoListe - Oppgavehåndtering for sak
 * Synkroniseres med Microsoft To Do
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './TodoListe.css';

export default function TodoListe({ sakId }) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visNyTodo, setVisNyTodo] = useState(false);
  const [redigerTodo, setRedigerTodo] = useState(null);
  const [filter, setFilter] = useState('aktive'); // 'alle', 'aktive', 'fullført'

  const [nyTodo, setNyTodo] = useState({
    title: '',
    notes: '',
    priority: 'normal',
    due_date: ''
  });

  useEffect(() => {
    if (sakId) {
      lastTodos();
    }
  }, [sakId]);

  const lastTodos = async () => {
    try {
      setLoading(true);
      const data = await crmApi.request(`/api/todos?sak_id=${sakId}`);
      setTodos(data.todos || []);
    } catch (error) {
      console.error('Feil ved lasting av TODOs:', error);
    } finally {
      setLoading(false);
    }
  };

  const opprettTodo = async (e) => {
    e.preventDefault();

    if (!nyTodo.title.trim()) {
      alert('Tittel er påkrevd');
      return;
    }

    try {
      await crmApi.request('/api/todos', {
        method: 'POST',
        body: JSON.stringify({
          sak_id: sakId,
          title: nyTodo.title,
          notes: nyTodo.notes,
          priority: nyTodo.priority,
          due_date: nyTodo.due_date ? new Date(nyTodo.due_date).getTime() : null
        })
      });

      await lastTodos();
      setNyTodo({ title: '', notes: '', priority: 'normal', due_date: '' });
      setVisNyTodo(false);
    } catch (error) {
      console.error('Feil ved oppretting av TODO:', error);
      alert('Kunne ikke opprette TODO');
    }
  };

  const oppdaterTodo = async (todoId, updates) => {
    try {
      await crmApi.request(`/api/todos/${todoId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      await lastTodos();
    } catch (error) {
      console.error('Feil ved oppdatering av TODO:', error);
      alert('Kunne ikke oppdatere TODO');
    }
  };

  const slettTodo = async (todoId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppgaven?')) {
      return;
    }

    try {
      await crmApi.request(`/api/todos/${todoId}`, {
        method: 'DELETE'
      });

      await lastTodos();
    } catch (error) {
      console.error('Feil ved sletting av TODO:', error);
      alert('Kunne ikke slette TODO');
    }
  };

  const toggleFullført = async (todo) => {
    const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
    await oppdaterTodo(todo.id, { status: newStatus });
  };

  const filtrerTodos = () => {
    if (filter === 'aktive') {
      return todos.filter(t => t.status !== 'completed');
    } else if (filter === 'fullført') {
      return todos.filter(t => t.status === 'completed');
    }
    return todos;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'normal': return '#3b82f6';
      case 'low': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical': return '🔴 Kritisk';
      case 'high': return '🟠 Høy';
      case 'normal': return '🔵 Normal';
      case 'low': return '⚪ Lav';
      default: return priority;
    }
  };

  const erForsinket = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const formaterDato = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredTodos = filtrerTodos();

  if (loading) {
    return (
      <div className="todo-liste-container">
        <div className="todo-loading">✅ Laster oppgaver...</div>
      </div>
    );
  }

  return (
    <div className="todo-liste-container">
      <div className="todo-header">
        <div className="todo-header-info">
          <h3>✅ Oppgaver ({filteredTodos.length})</h3>
          <p>Synkroniseres med Microsoft To Do</p>
        </div>

        <button
          className="btn-primary"
          onClick={() => setVisNyTodo(true)}
        >
          ➕ Ny oppgave
        </button>
      </div>

      <div className="todo-filter">
        <button
          className={filter === 'aktive' ? 'active' : ''}
          onClick={() => setFilter('aktive')}
        >
          Aktive ({todos.filter(t => t.status !== 'completed').length})
        </button>
        <button
          className={filter === 'fullført' ? 'active' : ''}
          onClick={() => setFilter('fullført')}
        >
          Fullført ({todos.filter(t => t.status === 'completed').length})
        </button>
        <button
          className={filter === 'alle' ? 'active' : ''}
          onClick={() => setFilter('alle')}
        >
          Alle ({todos.length})
        </button>
      </div>

      {filteredTodos.length === 0 ? (
        <div className="todo-empty-state">
          <p>📝 Ingen oppgaver funnet</p>
          <small>Opprett en ny oppgave for å komme i gang</small>
        </div>
      ) : (
        <div className="todo-liste">
          {filteredTodos.map((todo) => (
            <div
              key={todo.id}
              className={`todo-item ${todo.status === 'completed' ? 'completed' : ''} ${erForsinket(todo.due_date) && todo.status !== 'completed' ? 'overdue' : ''}`}
            >
              <div className="todo-item-main">
                <input
                  type="checkbox"
                  checked={todo.status === 'completed'}
                  onChange={() => toggleFullført(todo)}
                  className="todo-checkbox"
                />

                <div
                  className="todo-item-content"
                  onClick={() => setRedigerTodo(todo)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="todo-item-title">{todo.title}</div>
                  {todo.notes && (
                    <div className="todo-item-notes">{todo.notes}</div>
                  )}

                  <div className="todo-item-meta">
                    <span
                      className="todo-priority-badge"
                      style={{ backgroundColor: getPriorityColor(todo.priority) }}
                    >
                      {getPriorityLabel(todo.priority)}
                    </span>

                    {todo.due_date && (
                      <span className={`todo-due-date ${erForsinket(todo.due_date) && todo.status !== 'completed' ? 'overdue' : ''}`}>
                        📅 {formaterDato(todo.due_date)}
                        {erForsinket(todo.due_date) && todo.status !== 'completed' && ' (Forsinket)'}
                      </span>
                    )}

                    {todo.synced_to_microsoft && (
                      <span className="todo-sync-badge" title="Synkronisert med Microsoft To Do">
                        ☁️ Synced
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="todo-item-actions">
                <button
                  className="btn-icon"
                  onClick={() => setRedigerTodo(todo)}
                  title="Rediger"
                >
                  ✏️
                </button>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => slettTodo(todo.id)}
                  title="Slett"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ny TODO dialog */}
      {visNyTodo && (
        <div className="modal-overlay" onClick={() => setVisNyTodo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>➕ Ny oppgave</h2>

            <form onSubmit={opprettTodo}>
              <div className="form-group">
                <label>Tittel *</label>
                <input
                  type="text"
                  value={nyTodo.title}
                  onChange={(e) => setNyTodo({...nyTodo, title: e.target.value})}
                  placeholder="Hva skal gjøres?"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Notater</label>
                <textarea
                  value={nyTodo.notes}
                  onChange={(e) => setNyTodo({...nyTodo, notes: e.target.value})}
                  placeholder="Detaljer om oppgaven..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Prioritet</label>
                <select
                  value={nyTodo.priority}
                  onChange={(e) => setNyTodo({...nyTodo, priority: e.target.value})}
                >
                  <option value="low">⚪ Lav</option>
                  <option value="normal">🔵 Normal</option>
                  <option value="high">🟠 Høy</option>
                  <option value="critical">🔴 Kritisk</option>
                </select>
              </div>

              <div className="form-group">
                <label>Frist</label>
                <input
                  type="date"
                  value={nyTodo.due_date}
                  onChange={(e) => setNyTodo({...nyTodo, due_date: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setVisNyTodo(false);
                    setNyTodo({ title: '', notes: '', priority: 'normal', due_date: '' });
                  }}
                >
                  Avbryt
                </button>
                <button type="submit" className="btn-primary">
                  Opprett oppgave
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rediger TODO dialog */}
      {redigerTodo && (
        <div className="modal-overlay" onClick={() => setRedigerTodo(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>✏️ Rediger oppgave</h2>

            <form onSubmit={(e) => {
              e.preventDefault();
              oppdaterTodo(redigerTodo.id, {
                title: redigerTodo.title,
                notes: redigerTodo.notes,
                priority: redigerTodo.priority,
                due_date: redigerTodo.due_date ? new Date(redigerTodo.due_date).getTime() : null
              });
              setRedigerTodo(null);
            }}>
              <div className="form-group">
                <label>Tittel *</label>
                <input
                  type="text"
                  value={redigerTodo.title}
                  onChange={(e) => setRedigerTodo({...redigerTodo, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notater</label>
                <textarea
                  value={redigerTodo.notes || ''}
                  onChange={(e) => setRedigerTodo({...redigerTodo, notes: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Prioritet</label>
                <select
                  value={redigerTodo.priority}
                  onChange={(e) => setRedigerTodo({...redigerTodo, priority: e.target.value})}
                >
                  <option value="low">⚪ Lav</option>
                  <option value="normal">🔵 Normal</option>
                  <option value="high">🟠 Høy</option>
                  <option value="critical">🔴 Kritisk</option>
                </select>
              </div>

              <div className="form-group">
                <label>Frist</label>
                <input
                  type="date"
                  value={redigerTodo.due_date ? new Date(redigerTodo.due_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setRedigerTodo({...redigerTodo, due_date: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setRedigerTodo(null)}
                >
                  Avbryt
                </button>
                <button type="submit" className="btn-primary">
                  Lagre endringer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
